import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import KineticText from "../components/KineticText";
import useScrollReveal from "../hooks/useScrollReveal";

export default function About() {
  const r1 = useScrollReveal();
  const r2 = useScrollReveal();
  const r3 = useScrollReveal();

  return (
    <div data-testid="about-page" className="pt-32">
      <section className="max-w-[1600px] mx-auto px-6 lg:px-12">
        <p className="label-overline text-zinc-500">Studio · Ege Models</p>
        <h1 className="font-serif text-6xl md:text-7xl lg:text-9xl tracking-tighter font-light mt-6 leading-[0.85]">
          <KineticText text="Scouting." staggerMs={28}/><br/>
          <span className="italic text-zinc-600">
            <KineticText text="Developing." staggerMs={28}/>
          </span><br/>
          <KineticText text="Placing." staggerMs={28}/>
        </h1>
      </section>

      <section ref={r1} className="scroll-reveal max-w-[1600px] mx-auto px-6 lg:px-12 mt-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <p className="font-serif italic text-3xl text-zinc-700 leading-snug">
            "We work in <em>aesthetic diversity</em> — discovering the next generation
            of editorial faces, and developing them for the world."
          </p>
          <p className="label-overline text-zinc-500 mt-6">— Ege Models studio</p>
        </div>
        <div className="lg:col-span-6 lg:col-start-7 space-y-6 text-zinc-700 leading-relaxed">
          <p>
            Ege Models is a model management agency based in <b>Rotterdam</b>, the Netherlands. We focus on three things:
            <b> scouting</b> the next generation of editorial faces, <b>developing</b> their book and
            measurements, and <b>placing</b> them on editorial, runway, campaign and motion work — with our
            clients and with partner agencies abroad.
          </p>
          <p>
            Our roster is small by design and editorial-first by intent. We shoot polaroids,
            run runway sessions, build digital books, and mentor through the first
            placements. The result is careers that travel — between editorial, runway, campaign and motion.
          </p>
        </div>
      </section>

      {/* DISCIPLINE STRIP */}
      <section ref={r2} className="scroll-reveal max-w-[1600px] mx-auto px-6 lg:px-12 mt-32 grid grid-cols-1 md:grid-cols-3 gap-px bg-black/10 border-y border-black/10">
        {[
          { n: "01 / Scouting", d: "Open castings, social discovery, street scouting — we look for the canvas before the campaign. We sign by intention, not volume." },
          { n: "02 / Development", d: "Test shoots, polaroid books, runway training, mentorship. We invest in the talent's book and measurements before any international submission." },
          { n: "03 / Placement", d: "We place our roster on editorial, runway, campaign and motion work — in the Netherlands and, together with partner agencies, worldwide." },
        ].map((b) => (
          <div key={b.n} className="bg-[#FAFAFA] p-10">
            <p className="label-overline text-zinc-500">{b.n}</p>
            <p className="text-zinc-700 mt-5 leading-relaxed">{b.d}</p>
          </div>
        ))}
      </section>

      {/* TIMELINE */}
      <section ref={r3} className="scroll-reveal max-w-[1600px] mx-auto px-6 lg:px-12 mt-32 grid grid-cols-2 md:grid-cols-4 gap-px bg-black/10 border-y border-black/10">
        {[
          ["2026", "Founded · Rotterdam"],
          ["NL", "Office · Rotterdam"],
          ["3077 MG", "Puck van Heelstraat 142"],
          ["—", "Curatorial roster · always"],
        ].map(([y, t]) => (
          <div key={y} className="bg-[#FFFFFF] p-10">
            <p className="font-serif text-5xl tracking-tighter">{y}</p>
            <p className="label-overline text-zinc-500 mt-3">{t}</p>
          </div>
        ))}
      </section>

      {/* SUSTAINABILITY — condensed "less, but better" principles */}
      <section className="max-w-[1600px] mx-auto px-6 lg:px-12 mt-32">
        <p className="label-overline text-zinc-500">How we work</p>
        <h2 className="font-serif text-5xl lg:text-6xl tracking-tighter font-light mt-6 leading-[0.95]">
          Less, but <span className="italic text-zinc-600">better.</span>
        </h2>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-black/10 border border-black/10">
          {[
            ["Travel", "Rail first under 6 hours in Europe. Unavoidable flights offset through certified projects."],
            ["Production", "We partner with stylists and clients who source from archive, rental or existing wardrobe."],
            ["Studio", "Rotterdam office on 100% renewable electricity. No single-use plastics."],
            ["People", "Pay parity, mental health support and honest contracts — success measured in careers, not campaigns."],
          ].map(([t2, d]) => (
            <div key={t2} className="bg-white p-8">
              <p className="label-overline text-zinc-500">{t2}</p>
              <p className="text-zinc-700 mt-4 leading-relaxed text-sm">{d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1600px] mx-auto px-6 lg:px-12 mt-32 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <img src="https://images.pexels.com/photos/2876032/pexels-photo-2876032.jpeg?auto=compress&cs=tinysrgb&w=1200&q=85" alt="" className="w-full aspect-[4/5] object-cover" />
        <div>
          <p className="label-overline text-zinc-500">A studio you can speak to</p>
          <h2 className="font-serif text-5xl lg:text-6xl tracking-tighter font-light mt-6 leading-[0.95]">Care, craft<br/>& discretion.</h2>
          <p className="text-zinc-700 mt-8 leading-relaxed text-lg">
            Behind every placement is a relationship. Behind every relationship, a team that
            answers calls at 3 a.m., reads contracts twice, and shows up when it matters.
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/code-of-conduct" className="btn-primary">Code of Conduct <ArrowUpRight size={16}/></Link>
            <Link to="/contact" className="btn-ghost">Contact</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
